<script setup lang="ts">
import { twMerge } from "tailwind-merge";

import { ShapeFillColors } from "~/telestrations/constants/colors";
import { TELESTRATION_TOOLTIP_CONTAINER } from "~/telestrations/constants/tooltip";
import { ShapeType } from "~/telestrations/types/canvas";

const { t } = useI18n();
const shapesStore = useShapesStore();
const isMenuOpened = ref(false);

const options = computed(() => {
  if (shapesStore.selectedShape) {
    return ShapeFillColors[shapesStore.selectedShape.type];
  }

  return [];
});
const selectedFillColor = computed(() => {
  if (shapesStore.selectedShape) {
    return shapesStore.selectedShape.fill as string;
  }

  return "transparent";
});

const inputClasses = computed(() =>
  twMerge(
    "!border-none !gap-x-2 px-1.5",
    isMenuOpened.value ? "!bg-neutral-light-100 dark:!bg-neutral-dark-500" : "hover:!bg-neutral-light-100 dark:hover:!bg-neutral-dark-500"
  )
);

function onShow() {
  isMenuOpened.value = true;
}

function onHide() {
  isMenuOpened.value = false;
}

function onShapeFillChange(value: string) {
  if (!shapesStore.selectedShape) return;

  if (shapesStore.selectedShape.type !== ShapeType.Text) {
    if (value === "transparent") {
      shapesStore.updateShape(shapesStore.selectedShape, { fill: value });
      return;
    }

    shapesStore.updateShape(shapesStore.selectedShape, { fill: `${value}80` }); // Add 50% transparency
    return;
  }

  shapesStore.updateShape(shapesStore.selectedShape, { fill: value });
}
</script>

<template>
  <template v-if="options.length">
    <USelectMenu
      :model-value="selectedFillColor"
      :options="options"
      :input-class="inputClasses"
      list-class="grid grid-cols-2 p-2 gap-x-1 gap-y-2"
      item-class="min-h-max rounded"
      container-class="p-1"
      variant="inline"
      :divider="false"
      :hide-trailing-icon="true"
      :render-in-portal="false"
      @show="onShow"
      @hide="onHide"
      @update:model-value="onShapeFillChange"
    >
      <template #option="{ option }">
        <div
          v-if="option.value !== 'transparent'"
          class="rounded-full border-2 p-0.5 "
          :style="{ borderColor: selectedFillColor.slice(0, 7) === option.value ? option.value : 'transparent' }"
        >
          <div class="size-6 rounded-full" :style="{ backgroundColor: option.value }" />
        </div>
        <div
          v-else
          class="size-8 rounded-full border-2 p-0.5"
          :class="selectedFillColor === 'transparent' ? 'border-blue-500' : 'border-transparent'"
        >
          <UIcon class="size-6 rounded-full" name="no-shape-fill" />
        </div>
      </template>
      <div
        v-if="selectedFillColor !== 'transparent'"
        v-tooltip="{
          content: t('labels.change_fill_color'),
          container: TELESTRATION_TOOLTIP_CONTAINER,
        }"
        class="size-6 rounded-full"
        :style="{ backgroundColor: selectedFillColor }"
      />
      <UIcon
        v-else
        v-tooltip="{
          content: t('labels.change_fill_color'),
          container: TELESTRATION_TOOLTIP_CONTAINER,
        }"
        class="size-6 rounded-full"
        name="no-shape-fill"
      />
    </USelectMenu>
  </template>
</template>
